// components/GallerySection.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, GalleryVerticalIcon } from 'lucide-react';

const SkeletonLoader = ({ count = 12 }) => (
  <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="h-40 bg-gray-200 rounded-lg shadow-lg animate-pulse"
      ></div>
    ))}
  </div>
);

export default function GallerySection({ gallery, loading }) {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section id="gallery" className="w-full min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black pt-24">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
        </motion.div>

        {loading ? (
          <SkeletonLoader count={12} />
        ) : gallery.length === 0 ? (
            <div className="fixed inset-0   bg-black/100 backdrop-blur-sm z-[9999] flex flex-col items-center justify-center">
  {/* The Spinner Container */}
  <div className="relative w-20 h-20 mb-6">
    {/* Outer Glowing Ring */}
    <div className="absolute inset-0 rounded-full border-4 border-white/5"></div>
    
    {/* Spinning Gradient Ring */}
    <div className="absolute inset-0 rounded-full border-t-4 border-purple-500 animate-spin shadow-[0_0_15px_rgba(168,85,247,0.5)]"></div>
    
    {/* Inner Icon */}
    <div className="absolute inset-0 flex items-center justify-center">
      <GalleryVerticalIcon size={28} className="text-white/30 animate-pulse" />
    </div>
  </div>

  {/* Status Text */}
  <div className="text-center space-y-2">
    <p className="text-white/80 text-lg font-medium tracking-tight">
      Gallery is Loading
    </p>
    <p className="text-white/40 text-xs tracking-[0.2em] uppercase">
      Please wait a moment
    </p>
  </div>
</div>
        ) : (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 auto-rows-[160px]">
            {gallery.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="relative rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition cursor-pointer group h-full"
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image.url}
                  alt={`Gallery ${index}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition"></div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              className="relative max-w-4xl w-full"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-red-400 transition"
              >
                <X size={32} />
              </button>
              <img
                src={selectedImage.url}
                alt="Full"
                className="w-full rounded-lg shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}